import { LitElement, html, css, nothing, unsafeCSS } from "lit";
import { customElement, property, state, eventOptions } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { styleMap } from "lit/directives/style-map.js";
import {
  createGradient,
  darkShade,
  fadeMix,
  hoverShade,
  resolveColor,
  resolveSemanticSurfaceKey,
  semanticSoftSurfaceTokens,
  skyBorderPrimaryShorthand,
  tuneSurfaceVariantForDefaultColor,
} from "../helper/utils/color-resolver";
import { cssOnFillEnhance, contrastOverlay, ON_COLOR_FALLBACK_LIGHT } from "../helper/utils/contrast-color";
import { resolveRadius } from "../helper/utils/size-resolver";
import { PresetController } from "../helper/controllers/preset-controller";
import { skyFocusRingHostStyles } from "../helper/styles/focus-ring";
import { ifDefined } from "lit/directives/if-defined.js";
import { SkyIcon } from "../sky-icon/sky-icon.js";

export type SkyButtonVariant =
  | "solid"
  | "faded"
  | "bordered"
  | "transparent"
  | "flat"
  | "ghost"
  | "relief"
  | "gradient"
  | "shadow"
  | "link";
export type SkyButtonType = "button" | "submit" | "reset";
export type SkyButtonAnimation =
  | "auto"
  | "none"
  | "lift"
  | "push"
  | "scale"
  | "rotate"
  | "custom";

/**
 * @element sky-button
 *
 * @summary Styled button supporting variants, icons, loading state, ripple feedback, and form-associated behavior.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/button
 * @dependency sky-icon
 *
 * @slot - Default slot for the button label (replaces `label` property if used).
 *
 * @csspart button - The native `<button>` element.
 * @csspart icon - The optional left/right icons.
 * @csspart loading-overlay - Overlay displayed when loading.
 * @csspart spinner - Spinner displayed when loading.
 *
 * @property {SkyButtonVariant} variant - Visual variant of the button. Use `"link"` for text-only control with underline on hover. Default: `"solid"`.
 * @property {string} color - Base color token or custom CSS color. Default: `"primary"`.
 * @property {SkyButtonType} type - Button type attribute. Default: `"button"`.
 * @property {string} form - Associated form ID for custom submit handling.
 * @property {string} size - Preset size token (`xs…xl`) or CSS size (e.g., `"15px"`, `"1rem"`). Default: `"md"`.
 * @property {string} radius - Preset radius token (`none…full`) or CSS radius (e.g., `"12px"`). Default: `"md"`.
 * @property {string} label - Text label for the button. Ignored if default slot is used. Default: `"Button"`.
 * @property {boolean} disabled - Whether the button is disabled. Default: `false`.
 * @property {boolean} loading - Shows a loading overlay when true. Default: `false`.
 * @property {string} iconL - Optional left-side icon (Iconify name). Default: `""`.
 * @property {string} iconR - Optional right-side icon (Iconify name). Default: `""`.
 * @property {boolean} iconOnly - Square padding for icon-only buttons; hides label text. Default: `false`.
 * @property {string} colors - Comma-separated color stops for gradient variant. Default: `""`.
 * @property {SkyButtonAnimation} animation - Transform animation style. Default: `"auto"`.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @method focusButton Focuses the internal native button.
 * @method clickButton Triggers a click on the internal native button when enabled.
 *
 * @example
 * ```html
 * <sky-button variant="gradient" colors="linear@90deg: primary, secondary" iconl="ion:add" label="Create"></sky-button>
 * <sky-button preset="cancel" label="Cancel"></sky-button>
 * ```
 * ```vue
 * <template>
 *   <sky-button variant="gradient" colors="linear@90deg: primary, secondary" iconl="ion:add" label="Create"></sky-button>
 *   <sky-button preset="cancel" label="Cancel"></sky-button>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <>
 *       <sky-button variant="gradient" colors="linear@90deg: primary, secondary" iconl="ion:add" label="Create"></sky-button>
 *       <sky-button preset="cancel" label="Cancel"></sky-button>
 *     </>
 *   );
 * }
 * ```
 */
@customElement("sky-button")
export class SkyButton extends LitElement {

  static dependencies: Record<string, CustomElementConstructor> = {
    "sky-icon": SkyIcon,
  };

  static formAssociated = true;

  private internals: ElementInternals;
  /** Visual variant of the button. */
  @property({ type: String, reflect: true }) variant: SkyButtonVariant = "solid";

  /** Accepts preset tokens OR any valid CSS color (e.g., #0af, rgb(), var(--x)). */
  @property({ type: String }) color: string = "primary";

  /** Button type attribute. */
  @property({ type: String }) type: SkyButtonType = "button";

  /** Associated form ID for custom form handling. */
  @property({ type: String }) form = "";

  /** Preset tokens (`xs…xl`) OR CSS size like `"15px"`. */
  @property({ type: String }) size: string = "md";

  /** Preset tokens (`none…full`) OR any CSS border radius. */
  @property({ type: String }) radius: string = "md";

  /** Text label for the button. */
  @property({ type: String }) label = "Button";

  /** Disables the button. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Shows a loading spinner overlay. */
  @property({ type: Boolean, reflect: true }) loading = false;

  /** Left-side icon (Iconify icon name). */
  @property({ type: String }) iconL = "";

  /** Right-side icon (Iconify icon name). */
  @property({ type: String }) iconR = "";

  /** Square padding and compact layout when the button shows only an icon. */
  @property({ type: Boolean, reflect: true }) iconOnly = false;

  /** Colors for gradient variant. */
  @property({ type: String }) colors: string = "";

  /** Animation style for hover/active transforms. */
  @property({ type: String, reflect: true }) animation: SkyButtonAnimation = "auto";

  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  /** @internal Set by `sky-button-group` to flatten grouped button visuals. */
  @property({ type: Boolean, reflect: true }) inGroup = false;

  @state() private _hasMeaningfulSlot = false;

  /** Inline CSS variable cache. */
  private _styleVars: Record<string, string> = {};

  private _presets = new PresetController(this, {
    isPaused: () => this.inGroup,
    onChange: () => {
      this._styleDirty = true;
    },
  });

  private _lastPresetRevision = -1;
  /** Set when presets change so `update()` rebuilds CSS vars after hostUpdate. */
  private _styleDirty = true;

  static styles = [
    skyFocusRingHostStyles,
    css`
    :host {
      display: inline-block;
    }

    :host([disabled]) {
      cursor: not-allowed;
      pointer-events: none;
    }

    :host([in-group]) {
      align-self: stretch;
    }

    :host([iconOnly]) button {
      width: auto;
      min-width: var(--btn-min-width, auto);
      aspect-ratio: 1;
      gap: 0;
    }

    :host([variant="link"]) button {
      width: auto;
      border-radius: 0;
      text-decoration: none;
      background-color: transparent;
      background-image: linear-gradient(currentColor, currentColor);
      background-position: 0 100%;
      background-repeat: no-repeat;
      background-size: 0% 1px;
      transition:
        background-size var(--sky-duration-normal, 200ms) var(--sky-ease-default, ease),
        color var(--sky-duration-fast, 120ms) var(--sky-ease-default, ease),
        box-shadow var(--sky-duration-fast, 120ms) var(--sky-ease-default, ease),
        border-color var(--sky-duration-fast, 120ms) var(--sky-ease-default, ease),
        transform var(--sky-duration-fast, 120ms) var(--sky-ease-default, ease);
    }
    :host([variant="link"]) button:hover:not(:disabled),
    :host([variant="link"]) button:focus-visible:not(:disabled) {
      background-color: transparent;
      background-image: linear-gradient(currentColor, currentColor);
      background-position: 0 100%;
      background-repeat: no-repeat;
      background-size: 100% 1px;
    }

    button {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: var(--btn-radius, var(--sky-radius-primary));
      font: inherit;
      cursor: pointer;
      box-sizing: border-box;
      outline: none;
      border: var(
        --btn-border,
        var(--btn-border-width, 0) solid var(--btn-border-color, transparent)
      );
      background-color: var(--btn-bg-color, transparent);
      background-image: none;
      color: var(--btn-color);
      box-shadow: var(--btn-shadow, none);
      transform: var(--btn-transform, none);
      gap: var(--btn-gap, var(--sky-space-1, 4px));
      padding: var(--btn-padding, var(--sky-space-2, 8px) var(--sky-space-3, 14px));
      font-size: var(--btn-font-size, var(--sky-font-size-md, 1rem));
      font-family: var(--sky-font);
      white-space: nowrap;
      text-align: center;
      text-decoration: var(--btn-text-decoration, none);
      width: 100%;
      height: auto;
      min-height: var(--btn-min-height, auto);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      transition:
        background-color var(--sky-duration-fast, 120ms) var(--sky-ease-default, ease),
        border-color var(--sky-duration-fast, 120ms) var(--sky-ease-default, ease),
        color var(--sky-duration-fast, 120ms) var(--sky-ease-default, ease),
        box-shadow var(--sky-duration-fast, 120ms) var(--sky-ease-default, ease),
        transform var(--sky-duration-fast, 120ms) var(--sky-ease-out, ease),
        opacity var(--sky-duration-fast, 120ms) var(--sky-ease-default, ease);

      /* Ensure button doesn't shrink */
      flex-shrink: 0;
    }

    :host([variant="gradient"]) button {
      background: var(--btn-bg);
      background-color: transparent;
    }

    button:hover:not(:disabled),
    button:focus-visible:not(:disabled) {
      background-color: var(--btn-bg-color-hover, var(--btn-bg-color, transparent));
      color: var(--btn-color-hover, var(--btn-color));
      box-shadow: var(--btn-shadow-hover, var(--btn-shadow));
      border: var(
        --btn-border-hover,
        var(
          --btn-border,
          var(--btn-border-width, 0) solid
            var(--btn-border-color-hover, var(--btn-border-color, transparent))
        )
      );
      text-decoration: var(
        --btn-text-decoration-hover,
        var(--btn-text-decoration, none)
      );
      transform: var(--btn-transform-hover, var(--btn-transform, none));
      height: auto;
      min-height: var(--btn-min-height, auto);
    }

    :host([variant="gradient"]) button:hover:not(:disabled),
    :host([variant="gradient"]) button:focus-visible:not(:disabled) {
      background: var(--btn-bg-hover, var(--btn-bg));
    }
    button:focus-visible:not(:disabled) {
      box-shadow:
        var(--sky-focus-ring),
        var(--btn-shadow-hover, var(--btn-shadow, none));
    }
    button:active:not(:disabled) {
      transform: var(
        --btn-transform-active,
        var(--btn-transform-hover, var(--btn-transform, none))
      );
    }
    button:disabled {
      opacity: var(--sky-opacity-disabled, 0.5);
      cursor: not-allowed;
      pointer-events: none;
    }

    @media (prefers-reduced-motion: reduce) {
      button,
      :host([variant="link"]) button {
        transition: none;
      }

      button:active:not(:disabled) {
        transform: none;
      }

      .ripple {
        animation: none;
      }

      .spinner {
        animation: none;
      }
    }

    .ripple {
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.5);
      transform: scale(0);
      animation: ripple-animation 0.6s linear;
      pointer-events: none;
    }
    @keyframes ripple-animation {
      to {
        transform: scale(4);
        opacity: 0;
      }
    }
    sky-icon {
      display: inline-block;
      width: 1em;
      height: 1em;
      font-size: var(--icon-size, 18px);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
    .loading-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: var(--sky-overlay-secondary);
      visibility: hidden;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
    button[loading] .loading-overlay {
      visibility: visible;
    }
    .spinner {
      width: var(--spinner-size, 18px);
      height: var(--spinner-size, 18px);
      border: 2px solid currentColor;
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    /* Solid fills: enhance FG with contrast-color when supported (fallback remains --btn-color).
       Glass shadow variant is excluded. Ghost is enhanced only on hover (fill becomes solid). */
    ${unsafeCSS(
      cssOnFillEnhance([
        {
          selector:
            ':host(:is([variant="solid"], [variant="relief"], [variant="gradient"]):not([data-text-explicit])) button',
          fillVar: "--btn-bg-color",
        },
        {
          selector:
            ':host(:is([variant="solid"], [variant="relief"], [variant="gradient"]):not([data-text-explicit])) button sky-icon',
          fillVar: "--btn-bg-color",
        },
        {
          selector:
            ':host(:is([variant="solid"], [variant="relief"], [variant="gradient"]):not([data-text-explicit])) button:hover:not(:disabled), :host(:is([variant="solid"], [variant="relief"], [variant="gradient"]):not([data-text-explicit])) button:focus-visible:not(:disabled)',
          fillVar: "var(--btn-bg-color-hover, var(--btn-bg-color))",
        },
        {
          selector:
            ':host(:is([variant="solid"], [variant="relief"], [variant="gradient"]):not([data-text-explicit])) button:hover:not(:disabled) sky-icon, :host(:is([variant="solid"], [variant="relief"], [variant="gradient"]):not([data-text-explicit])) button:focus-visible:not(:disabled) sky-icon',
          fillVar: "var(--btn-bg-color-hover, var(--btn-bg-color))",
        },
        {
          selector:
            ':host([variant="ghost"]:not([data-text-explicit])) button:hover:not(:disabled), :host([variant="ghost"]:not([data-text-explicit])) button:focus-visible:not(:disabled)',
          fillVar: "var(--btn-bg-color-hover, var(--btn-bg-color))",
        },
        {
          selector:
            ':host([variant="ghost"]:not([data-text-explicit])) button:hover:not(:disabled) sky-icon, :host([variant="ghost"]:not([data-text-explicit])) button:focus-visible:not(:disabled) sky-icon',
          fillVar: "var(--btn-bg-color-hover, var(--btn-bg-color))",
        },
      ]),
    )}
  `,
  ];

  constructor() {
    super();
    // @ts-ignore
    this.internals = this.attachInternals();
  }

  private _slotHasMeaningfulContent(slotEl: HTMLSlotElement): boolean {
    const nodes = slotEl.assignedNodes({ flatten: true });
    return nodes.some((n) =>
      n.nodeType === Node.ELEMENT_NODE
        ? true
        : (n.textContent ?? "").trim().length > 0
    );
  }

  // Slot change handler (not a lifecycle hook).
  private _onSlotChange = (e: Event) => {
    const slot = e.target as HTMLSlotElement;
    const next = this._slotHasMeaningfulContent(slot);
    // Only set state if it actually changed to avoid extra updates
    if (next !== this._hasMeaningfulSlot) this._hasMeaningfulSlot = next;
  };

  /** Uses vertical padding for equal sides (icon-only square control). */
  private _toSquarePadding(padding: string): string {
    const py = parseFloat(padding.trim().split(/\s+/)[0]);
    const n = Number.isFinite(py) ? py : 8;
    return `${n}px`;
  }

  private _resolveSize(input: string): {
    padding: string;
    fontSize: string;
    gap: string;
    icon: string;
    spinner: string;
  } {
    const presets: Record<
      string,
      {
        padding: string;
        fontSize: string;
        gap: string;
        icon: string;
        spinner: string;
      }
    > = {
      xs: {
        padding: "3px 7px",
        fontSize: "12px",
        gap: "3px",
        icon: "14px",
        spinner: "14px",
      },
      sm: {
        padding: "5px 9px",
        fontSize: "14px",
        gap: "4px",
        icon: "16px",
        spinner: "16px",
      },
      md: {
        padding: "8px 14px",
        fontSize: "16px",
        gap: "5px",
        icon: "18px",
        spinner: "18px",
      },
      lg: {
        padding: "10px 18px",
        fontSize: "18px",
        gap: "6px",
        icon: "20px",
        spinner: "20px",
      },
      xl: {
        padding: "14px 24px",
        fontSize: "20px",
        gap: "8px",
        icon: "24px",
        spinner: "24px",
      },
    };
    if (presets[input]) return presets[input];

    const m = String(input)
      .trim()
      .match(/^([\d.]+)(px|rem|em)?$/i);
    const unit = m?.[2] || "px";
    const fsNum = m ? parseFloat(m[1]) : 16;
    const fs = `${fsNum}${unit}`;

    const to = (n: number) => `${Math.round(n * 100) / 100}${unit}`;
    const clamp = (n: number, min: number, max: number) =>
      Math.max(min, Math.min(max, n));

    const py = clamp(fsNum * 0.55, 4, 18);
    const px = clamp(fsNum * 0.9, 8, 28);
    const gap = clamp(fsNum * 0.3, 3, 10);
    const icon = clamp(fsNum * 1.125, 12, 28);
    const spinner = icon;

    return {
      padding: `${to(py)} ${to(px)}`,
      fontSize: fs,
      gap: to(gap),
      icon: to(icon),
      spinner: to(spinner),
    };
  }

  /** Theme tokens like `var(--sky-border-primary)` are full border shorthands, not colors. */
  private _isThemeBorderShorthand(border: string | undefined): boolean {
    if (!border || border === "none") return false;
    return /^var\(--sky-border-/.test(border.trim());
  }

  /** Extracts the color token from a `Npx solid …` border shorthand. */
  private _borderColorFromShorthand(border: string | undefined): string {
    if (!border || border === "none" || this._isThemeBorderShorthand(border)) {
      return "transparent";
    }
    const match = border.match(/solid\s+(.+)$/);
    return match?.[1]?.trim() ?? "transparent";
  }

  /**
   * Lit order: `willUpdate` → controllers `hostUpdate` (presets) → `update`/render.
   * Style vars must be built in `update()` so they see preset-applied props.
   * Building in `willUpdate` left render with `{}` after PresetController cleared/wrote props.
   */
  protected override update(changedProperties: Map<string, unknown>): void {
    this._rebuildStyleVars(changedProperties);
    super.update(changedProperties);
  }

  private _rebuildStyleVars(changedProperties: Map<string, unknown>): void {
    const styleProps = [
      "color",
      "variant",
      "size",
      "radius",
      "animation",
      "preset",
      "inGroup",
      "disabled",
      "loading",
      "colors",
      "iconOnly",
    ];
    const presetChanged = this._presets.revision !== this._lastPresetRevision;
    const needsStyleUpdate =
      this._styleDirty ||
      presetChanged ||
      styleProps.some((p) => changedProperties.has(p));
    if (!needsStyleUpdate && Object.keys(this._styleVars).length > 0) {
      return;
    }
    this._lastPresetRevision = this._presets.revision;
    this._styleDirty = false;

    const baseColor = resolveColor(this.color);
    const textColor = ON_COLOR_FALLBACK_LIGHT;

    const variantDefaults = {
      solid: {
        bg: baseColor,
        color: textColor,
        border: "none",
        shadow: "none",
        hoverBg: baseColor,
        hoverColor: textColor,
        hoverShadow: "var(--sky-box-shadow-hover)",
      },
      faded: {
        bg: "var(--sky-hover-tertiary)",
        color: baseColor,
        border: "1px solid var(--sky-text-tertiary)",
        shadow: "none",
        hoverBg: "var(--sky-hover-secondary)",
        hoverColor: baseColor,
        hoverShadow: "none",
        hoverBorder: "1px solid var(--sky-text-secondary)",
      },
      bordered: {
        bg: "transparent",
        color: baseColor,
        border: `2px solid ${baseColor}`,
        shadow: "none",
        hoverBg: "transparent",
        hoverColor: baseColor,
        hoverShadow: "none",
        hoverBorder: `2px solid ${hoverShade(baseColor, 25)}`,
      },
      transparent: {
        bg: "transparent",
        color: baseColor,
        border: "none",
        shadow: "none",
        hoverBg: fadeMix(baseColor, 15),
        hoverColor: baseColor,
        hoverShadow: "none",
      },
      flat: {
        bg: fadeMix(baseColor, 20),
        color: hoverShade(baseColor, 30),
        border: "none",
        shadow: "none",
        hoverBg: fadeMix(baseColor, 25),
        hoverColor: hoverShade(baseColor, 35),
        hoverShadow: "none",
      },
      ghost: {
        bg: "transparent",
        color: baseColor,
        border: `2px solid ${baseColor}`,
        shadow: "none",
        hoverBg: baseColor,
        hoverColor: textColor,
        hoverShadow: "none",
      },
      shadow: {
        bg: "var(--sky-glass-primary)",
        color: "var(--sky-text-primary)",
        border: skyBorderPrimaryShorthand(),
        shadow: "none",
        hoverBg: "var(--sky-glass-primary)",
        hoverColor: baseColor,
        hoverShadow: "var(--sky-box-shadow-hover)",
        hoverBorder: skyBorderPrimaryShorthand(),
      },
      relief: {
        bg: baseColor,
        color: textColor,
        border: "none",
        shadow: `
        inset 0 1px 0 rgba(255,255,255,0.4),
        0 3px 0 ${darkShade(baseColor, 40)},
        0 4px 6px -1px ${fadeMix(baseColor, 40)}
      `,
        hoverBg: baseColor,
        hoverColor: textColor,
        hoverShadow: "none",
      },
      gradient: {
        bg: createGradient(this.colors, baseColor),
        color: textColor,
        border: "none",
        shadow: "none",
        hoverBg: createGradient(this.colors, baseColor),
        hoverColor: textColor,
        hoverShadow: "none",
      },
      link: {
        bg: "transparent",
        color: baseColor,
        border: "none",
        shadow: "none",
        hoverBg: "transparent",
        hoverColor: baseColor,
        hoverShadow: "none",
      },
    } as const;

    const transformsAuto = {
      solid: { hover: "translateY(-3px)", active: "translateY(-3px)" },
      faded: {},
      bordered: {},
      transparent: {},
      flat: {},
      ghost: {},
      shadow: { hover: "translateY(-3px)", active: "translateY(-3px)" },
      gradient: {},
      relief: { hover: "translateY(2px)", active: "translateY(2px)" },
      link: {},
    } as const;

    const presetToTransform = (p: typeof this.animation) => {
      switch (p) {
        case "none":
          return { hover: "none", active: "none" };
        case "lift":
          return { hover: "translateY(-2px)", active: "translateY(-1px)" };
        case "push":
          return { hover: "translateY(2px)", active: "translateY(2px)" };
        case "scale":
          return { hover: "scale(1.05)", active: "scale(1.02)" };
        case "rotate":
          return { hover: "rotate(3deg)", active: "rotate(2deg)" };
        case "custom":
          return {
            hover: "var(--btn-hover-transform, none)",
            active:
              "var(--btn-active-transform, var(--btn-hover-transform, none))",
          };
        default:
          return {};
      }
    };

    const effVariant = this.variant;
    const effColor = this.color;
    const effSize = this.size;
    const effRadius = this.radius;
    const effAnimation = this.animation;

    const current = (
      effVariant in variantDefaults ? effVariant : "solid"
    ) as keyof typeof variantDefaults;
    let v = { ...variantDefaults[current] };
    Object.assign(
      v,
      tuneSurfaceVariantForDefaultColor(effColor, current, variantDefaults[current]),
    );

    const semanticKey = resolveSemanticSurfaceKey(effColor);
    if (semanticKey && (current === "flat" || current === "faded")) {
      const soft = semanticSoftSurfaceTokens(semanticKey, {
        bordered: current === "faded",
      });
      Object.assign(v, {
        bg: soft.bg,
        color: soft.color,
        border: current === "faded" ? soft.border : "none",
        hoverBg: "var(--sky-hover-primary)",
        hoverColor: soft.color,
        hoverShadow: "none",
        ...(current === "faded" ? { hoverBorder: soft.border } : {}),
      });
    }

    const animationMode = this.inGroup ? "none" : effAnimation;
    const chosen =
      animationMode === "auto"
        ? transformsAuto[current]
        : presetToTransform(animationMode);
    const hoverTransform = (chosen as any).hover ?? "none";
    const activeTransform = (chosen as any).active ?? hoverTransform ?? "none";

    const s = this._resolveSize(effSize);
    const basePadding = this.iconOnly ? this._toSquarePadding(s.padding) : s.padding;
    const baseGap = this.iconOnly ? "0" : s.gap;

    let borderWidth = 0;
    const hasBorder2 = effVariant === "bordered" || effVariant === "ghost";
    const hasBorder1 = effVariant === "faded" || effVariant === "shadow";

    if (hasBorder2) {
      borderWidth = 2;
    } else if (hasBorder1) {
      borderWidth = 1;
    } else if (v.border && v.border.includes("px")) {
      const match = v.border.match(/(\d+)px/);
      borderWidth = match ? parseInt(match[1]) : 0;
    }

    let hoverBorderWidth = borderWidth;
    if ((v as any).hoverBorder && (v as any).hoverBorder.includes("px")) {
      const match = (v as any).hoverBorder.match(/(\d+)px/);
      hoverBorderWidth = match ? parseInt(match[1]) : borderWidth;
    }

    // Reduce padding on bordered variants to preserve outer height.
    let adjustedPadding = effVariant === "link" ? "0" : basePadding;
    if (borderWidth > 0) {
      // Parse the original padding
      const paddingParts = basePadding.split(" ");
      if (paddingParts.length === 1) {
        const all = parseFloat(paddingParts[0]);
        const adjusted = Math.max(all - borderWidth, 2);
        adjustedPadding = `${adjusted}px`;
      } else if (paddingParts.length === 2) {
        // Format: "vertical horizontal"
        const vertical = parseFloat(paddingParts[0]);
        const horizontal = parseFloat(paddingParts[1]);

        // Reduce vertical padding by border width to maintain same content height
        const adjustedVertical = Math.max(vertical - borderWidth, 2);
        adjustedPadding = this.iconOnly
          ? `${adjustedVertical}px`
          : `${adjustedVertical}px ${horizontal}px`;
      } else if (paddingParts.length === 4) {
        // Format: "top right bottom left"
        const top = parseFloat(paddingParts[0]);
        const right = parseFloat(paddingParts[1]);
        const bottom = parseFloat(paddingParts[2]);
        const left = parseFloat(paddingParts[3]);

        // Reduce top/bottom padding by border width
        const adjustedTop = Math.max(top - borderWidth, 2);
        const adjustedBottom = Math.max(bottom - borderWidth, 2);
        adjustedPadding = `${adjustedTop}px ${right}px ${adjustedBottom}px ${left}px`;
      }
    }

    const fontSize = parseFloat(s.fontSize);
    const iconPx = parseFloat(s.icon);
    let btnMinHeight: string;
    let btnMinWidth: string | undefined;
    if (effVariant === "link") {
      btnMinHeight = "auto";
    } else if (this.iconOnly) {
      const pad = parseFloat(adjustedPadding) || 0;
      const side = pad * 2 + iconPx + borderWidth * 2;
      btnMinHeight = `${side}px`;
      btnMinWidth = `${side}px`;
    } else {
      const paddingValues = adjustedPadding.split(" ");
      const verticalPadding =
        paddingValues.length >= 2
          ? parseFloat(paddingValues[0]) +
          parseFloat(paddingValues[2] || paddingValues[0])
          : parseFloat(paddingValues[0]) * 2 || 16;
      const baseHeight = verticalPadding + fontSize + 4 + borderWidth * 2;
      btnMinHeight = `${baseHeight}px`;
    }

    const hoverBorderShorthand = (v as { hoverBorder?: string }).hoverBorder;
    const usesThemeBorder =
      this._isThemeBorderShorthand(v.border) ||
      this._isThemeBorderShorthand(hoverBorderShorthand);
    const borderColor = this._borderColorFromShorthand(v.border);
    const borderColorHover = this._borderColorFromShorthand(
      hoverBorderShorthand ?? v.border,
    );

    const vars: Record<string, string> = {
      "--btn-color": v.color,
      "--btn-shadow": v.shadow,
      "--btn-radius":
        effVariant === "link" ? "0" : resolveRadius(effRadius),
      "--btn-color-hover": v.hoverColor,
      "--btn-shadow-hover": v.hoverShadow,
      "--btn-transform": "none",
      "--btn-transform-hover": hoverTransform,
      "--btn-transform-active": activeTransform,
      "--btn-padding": adjustedPadding, // Use adjusted padding!
      "--btn-font-size": s.fontSize,
      "--btn-gap": baseGap,
      "--icon-size": s.icon,
      "--spinner-size": s.spinner,

      // Set min-height to maintain consistent visual height
      "--btn-min-height": btnMinHeight,
      ...(btnMinWidth ? { "--btn-min-width": btnMinWidth } : {}),

      // Keep border width variables
      "--btn-border-width": `${borderWidth}px`,
      "--btn-border-width-hover": `${hoverBorderWidth}px`,

      // Focus ring follows `color` (not global active-primary).
      "--sky-focus-ring-color": baseColor,
    };

    if (usesThemeBorder) {
      vars["--btn-border"] = v.border;
      vars["--btn-border-hover"] = hoverBorderShorthand ?? v.border;
    } else {
      vars["--btn-border-color"] = borderColor;
      vars["--btn-border-color-hover"] = borderColorHover;
    }

    if (effVariant === "gradient") {
      vars["--btn-bg"] = v.bg;
      vars["--btn-bg-hover"] = v.hoverBg;
      // Opaque stop for contrast-color() — gradient paint still uses --btn-bg.
      vars["--btn-bg-color"] = baseColor;
      vars["--btn-bg-color-hover"] = baseColor;
    } else {
      vars["--btn-bg-color"] = v.bg;
      vars["--btn-bg-color-hover"] = v.hoverBg;
    }

    if (this.inGroup) {
      // In grouped mode, do not apply sky-button's own hover skin.
      // The container group controls visual interaction consistency.
      if (effVariant === "gradient") {
        // One gradient is painted on sky-button-group's `.group`; segments stay clear
        // (same idea as `::slotted(button)` transparent fills for native buttons).
        vars["--btn-bg"] = "transparent";
        vars["--btn-bg-hover"] = "color-mix(in srgb, white 8%, transparent)";
      } else {
        vars["--btn-bg-color-hover"] = vars["--btn-bg-color"] ?? "transparent";
      }
      vars["--btn-color-hover"] = vars["--btn-color"];
      if (usesThemeBorder) {
        vars["--btn-border-hover"] = vars["--btn-border"] ?? v.border;
      } else {
        vars["--btn-border-color-hover"] = vars["--btn-border-color"] ?? borderColor;
      }
      vars["--btn-shadow-hover"] = "none";
      if (effVariant === "relief") {
        vars["--btn-transform-hover"] = "translateY(2px)";
        vars["--btn-transform-active"] = "translateY(2px)";
      } else {
        vars["--btn-transform-hover"] = "none";
        vars["--btn-transform-active"] = "none";
      }
    }

    this._styleVars = vars;
  }

  /**
   * Focuses the internal native button.
   *
   * @returns {void}
   */
  public focusButton(): void {
    this.renderRoot.querySelector<HTMLButtonElement>("button")?.focus();
  }

  /**
   * Triggers a click on the internal native button when enabled.
   *
   * @returns {void}
   */
  public clickButton(): void {
    if (this.disabled || this.loading) return;
    this.renderRoot.querySelector<HTMLButtonElement>("button")?.click();
  }

  private _createRipple(event: MouseEvent): void {
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    const ripple = document.createElement("span");

    const size = Math.max(button.offsetWidth, button.offsetHeight);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;

    const bg = getComputedStyle(button).backgroundColor || "";
    ripple.style.background = contrastOverlay(bg);

    ripple.className = "ripple";
    const existing = button.querySelectorAll(".ripple");
    const maxRipples = 3;
    if (existing.length >= maxRipples && existing[0]) {
      existing[0].remove();
    }
    button.appendChild(ripple);
    ripple.addEventListener("animationend", () => ripple.remove());
  }

  formDisabledCallback(disabled: boolean): void {
    this.disabled = disabled;
  }

  get formOwner(): HTMLFormElement | null {
    if (typeof document === "undefined") return null;
    const formId = typeof this.form === "string" ? this.form.trim() : "";
    return (
      this.internals.form ||
      (formId ? (document.getElementById(formId) as HTMLFormElement | null) : null) ||
      this.closest("form")
    );
  }

  @eventOptions({ passive: false })
  private _handleClick(event: MouseEvent): void {
    if (this.disabled || this.loading) {
      event.preventDefault();
      return;
    }

    // If button type is "button", prevent any default navigation-like behavior
    if (this.type === "button") {
      event.preventDefault();
    }

    this._createRipple(event);

    const form = this.formOwner;
    if (this.type === "submit") {
      form?.requestSubmit();
      return;
    }
    if (this.type === "reset") {
      form?.reset();
      return;
    }
  }

  private _iconOnlyName(): string {
    return this.iconL || this.iconR;
  }

  override render() {
    const iconOnlyName = this._iconOnlyName();
    const ariaLabel =
      this.iconOnly && (this.label || iconOnlyName)
        ? this.label || iconOnlyName
        : undefined;

    return html`
      <button
        part="button"
        style=${styleMap(this._styleVars)}
        type=${this.type}
        form=${ifDefined(this.form ?? undefined)}
        aria-label=${ifDefined(ariaLabel)}
        ?disabled=${this.disabled || this.loading}
        ?loading=${this.loading}
        @click=${this._handleClick}
      >
        ${when(
          this.iconOnly,
          () =>
            when(
              iconOnlyName,
              () => html`<sky-icon part="icon" icon="${iconOnlyName}"></sky-icon>`,
            ),
          () => html`
            ${when(this.iconL, () => html`<sky-icon part="icon" icon="${this.iconL}"></sky-icon>`)}
            <slot @slotchange=${this._onSlotChange}></slot>
            ${this._hasMeaningfulSlot ? nothing : this.label}
            ${when(this.iconR, () => html`<sky-icon part="icon" icon="${this.iconR}"></sky-icon>`)}
          `,
        )}
        ${when(
          this.loading,
          () => html`
            <div class="loading-overlay" part="loading-overlay">
              <div class="spinner" part="spinner"></div>
            </div>
          `,
        )}
      </button>
    `;
  }
}
